import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { formatNumber, formatPercent, formatRupiahCompact } from '@/lib/format'
import { SectionCard } from '@/components/dashboard/section-card'
import { SegmentedControl } from '@/components/dashboard/segmented-control'
import { DonutChart } from '@/components/charts/donut-chart'
import { PLAN_BREAKDOWN } from '@/data/customers'
import { PLANS } from '@/data/plans'

interface PlanDistributionCardProps {
    className?: string
}

type Metric = 'customers' | 'mrr'

export const PlanDistributionCard: FC<PlanDistributionCardProps> = ({ className }): ReactNode => {
    const [metric, setMetric] = useState<Metric>('customers')
    const [activeKey, setActiveKey] = useState<string | null>(null)

    const segments = PLANS.map((plan) => {
        const breakdown = PLAN_BREAKDOWN.find((item) => item.plan === plan.id)

        return {
            key: plan.id,
            label: plan.name,
            value: metric === 'customers' ? breakdown?.customers ?? 0 : breakdown?.mrr ?? 0,
            color: plan.color
        }
    })

    const total = segments.reduce((sum, segment) => sum + segment.value, 0)
    const format = (value: number): string => (metric === 'customers' ? formatNumber(value) : formatRupiahCompact(value))
    const share = (value: number): number => (total === 0 ? 0 : (value / total) * 100)
    const active = segments.find((segment) => segment.key === activeKey)

    return (
        <SectionCard
            className={className}
            title='Distribusi paket'
            description='Komposisi pelanggan berbayar menurut paket.'
            action={(
                <SegmentedControl
                    size='sm'
                    label='Ukuran distribusi paket'
                    value={metric}
                    onChange={setMetric}
                    options={[
                        { value: 'customers', label: 'Pelanggan' },
                        { value: 'mrr', label: 'MRR' }
                    ]}
                />
            )}
        >
            <div className='flex flex-col items-center gap-6 sm:flex-row sm:gap-10 xl:flex-col xl:gap-6'>
                <DonutChart
                    className='max-w-[200px] shrink-0'
                    segments={segments}
                    activeKey={activeKey}
                    onActiveChange={setActiveKey}
                    centerValue={active ? format(active.value) : format(total)}
                    centerLabel={active ? `${active.label}, ${formatPercent(share(active.value))}` : metric === 'customers' ? 'pelanggan berbayar' : 'MRR total'}
                    ariaLabel={`Distribusi ${metric === 'customers' ? 'pelanggan' : 'MRR'} per paket: ${segments.map((segment) => `${segment.label} ${formatPercent(share(segment.value))}`).join(', ')}`}
                />

                <ul className='w-full min-w-0 space-y-1'>
                    {segments.map((segment) => (
                        <li key={segment.key}>
                            <button
                                type='button'
                                onPointerEnter={() => setActiveKey(segment.key)}
                                onPointerLeave={() => setActiveKey(null)}
                                onFocus={() => setActiveKey(segment.key)}
                                onBlur={() => setActiveKey(null)}
                                className={cn(
                                    'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
                                    activeKey === segment.key ? 'bg-muted' : 'hover:bg-muted/60'
                                )}
                            >
                                <span className='size-2.5 shrink-0 rounded-[3px]' style={{ background: segment.color }} aria-hidden='true' />
                                <span className='min-w-0 flex-1 truncate font-medium text-foreground'>
                                    {segment.label}
                                </span>
                                <span className='text-muted-foreground tabular-nums'>
                                    {formatPercent(share(segment.value))}
                                </span>
                                <span className='w-20 text-right font-semibold text-foreground tabular-nums'>
                                    {format(segment.value)}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </SectionCard>
    )
}
